/* eslint-disable camelcase */
import type { ProductClickViewType, SectionNameType } from './dataLayer.type';
import type { VendorEvent } from '../../types/global';
import type { UserContextType } from '../context/dynamic/UserContext';
import type {
  CartEntryFieldsFragment,
  CartMutation,
  CartProduct,
  Country,
  Maybe,
  Product,
} from '../graphql/codegen';

import { getCookie } from 'cookies-next';

import { PageTypes } from '../constants';
import { COCO, RSU } from '../constants/cookies';
import {
  CTL,
  PRODUCT_TILE_TYPE,
  RV,
  YMAL,
  YMAL_PLP,
  YMAL_PDP,
  YMAL_SEARCH,
  YMAL_HP,
  PLP,
  SRP,
  YMAL_CAMPAIGN,
  YMAL_STORIES,
  PLP_RBP,
  WLP,
} from '../constants/productTileTypes';
import { PRODUCT_CODE_REGEXP } from '../constants/regex';
import { localisedData, storageAvailable } from '../helpers';
import type { AlgoliaProductHit } from '~/types/algolia';

export interface EventProduct {
  name?: string | null;
  materialNumber?: string | null;
  materialGroup?: string | null;
  ecomm_prodid?: string | null;
  staticCategoryPath?: string | null;
  staticCategoryPathIds?: string | null;
  styleFamily?: string | null;
  mainColor?: string | null;
  mainColorHex?: string | null;
  ean?: string | null;
  comingSoon?: boolean | null;
  programme?: string | null;
  size?: string | null;
  fit?: string | null;
  viewType?: string | null;
  availability?: string | null;
  fromPrice?: number | null;
  basePrice?: number | null;
  finalPrice?: number | null;
  sale?: string | null;
  subTargetAudience?: string | null;
  quantity?: number | null;
  removalType?: string | null;
  position?: number | null;
}

export type SearchTrackingType = 'click' | 'enter' | 'page loaded';

type EventType =
  | 'eecProductDetailView'
  | 'eecAddToCart'
  | 'eecRemoveFromCart'
  | 'eecProductClick'
  | 'eecImpressions';

const parseProduct = (product: Product | AlgoliaProductHit, fromProductList = false) => {
  const {
    stockNotificationMessageEN,
    price,
    ean,
    code,
    fitEN,
    fromPrice,
    mainColor,
    materialGroup,
    baseProduct,
    nameEN,
    programEN,
    size,
    staticCategoryPath,
    staticCategoryPathIds,
    subTargetAudience,
    baseProductStockNotificationMessageEN,
    styleFamilyEN,
    oneSize,
    isGiftCard,
    sizeInformation,
  } = product as Product;
  const {
    fromPriceValue: fromPriceValueAlgolia,
    priceValue: priceValueAlgolia,
    stockNotificationMessage: stockNotificationMessageAlgolia,
    'baseProduct.code': baseProductCodeAlgolia,
    'baseProduct.stockNotificationMessage': baseProductStockNotificationMessageAlgolia,
    size: sizeAlgolia,
    fit: fitAlgolia,
    colour: mainColorAlgolia,
    name: nameAlgolia,
    program: programAlgolia,
    style: styleFamilyAlgolia,
    materialgroup,
  } = product as AlgoliaProductHit;

  const fromPriceValue = fromPrice?.value || fromPriceValueAlgolia;
  const priceValue = price?.value || priceValueAlgolia;
  const baseProductCode = baseProduct?.toUpperCase() || baseProductCodeAlgolia;
  const sizeValueString = price?.value ? size?.value?.toUpperCase() : sizeAlgolia;
  const baseProductStockNotificationMessage =
    baseProductStockNotificationMessageEN ||
    localisedData(product as AlgoliaProductHit, baseProductStockNotificationMessageAlgolia, 'EN');
  const stockNotificationMessage =
    stockNotificationMessageEN ||
    localisedData(product as AlgoliaProductHit, stockNotificationMessageAlgolia, 'EN');
  const fit = fitEN || localisedData(product as AlgoliaProductHit, fitAlgolia, 'EN');
  const color =
    mainColor?.descriptionEN ||
    mainColor?.description ||
    localisedData(product as AlgoliaProductHit, mainColorAlgolia, 'EN');
  const materialGroupValue =
    materialGroup || localisedData(product as AlgoliaProductHit, materialgroup, 'EN');
  const name = nameEN || localisedData(product as AlgoliaProductHit, nameAlgolia, 'EN');
  const programme = programEN || localisedData(product as AlgoliaProductHit, programAlgolia, 'EN');
  const styleFamily =
    styleFamilyEN || localisedData(product as AlgoliaProductHit, styleFamilyAlgolia, 'EN');

  const sale = fromPriceValue !== priceValue;
  const fromPriceDl = sale ? { fromPrice: fromPriceValue } : {};

  const prodid = code?.toUpperCase() || baseProductCode;
  let ecommProdid = {};
  let sizeValue = {};
  let eanValue = {};

  if (oneSize || isGiftCard) {
    // sizeInformation is not available on PLP/SRP but available on PDP
    // ean on PDP is available only when accessing the onesize product as product variant
    // for PDP(solr) base product - sizeInformation?.sizes?.at(0).ean
    // for PDP(solr) variant product - ean
    // for PLP/SRP(algolia) - ean
    const oneSizeEan = sizeInformation?.sizes?.at(0)?.ean ?? ean ?? null;

    ecommProdid = { ecomm_prodid: (!baseProductCode ? `${code}-PC` : code)?.toUpperCase() };
    sizeValue = { size: 'PC' };
    eanValue = oneSizeEan ? { ean: oneSizeEan } : {};
  } else if (!fromProductList) {
    ecommProdid = sizeValueString ? { ecomm_prodid: prodid } : {};
    eanValue = ean ? { ean } : {};
    sizeValue = sizeValueString ? { size: sizeValueString } : {};
  }

  return {
    availability: baseProductStockNotificationMessage || stockNotificationMessage || 'unknown',
    basePrice: priceValue,
    ...eanValue,
    ...ecommProdid,
    finalPrice: priceValue,
    fit,
    ...fromPriceDl,
    mainColor: color,
    materialGroup: materialGroupValue,
    materialNumber: baseProductCode || code?.toUpperCase(),
    name,
    programme,
    sale: sale.toString(),
    ...sizeValue,
    staticCategoryPath,
    staticCategoryPathIds,
    styleFamily,
    subTargetAudience,
  };
};

type CoreEecEventObject = {
  events: {
    category: 'ecommerce';
    action: EventType;
    label: undefined;
  };
  event: EventType;
};

const makeEventObject = (eventType: EventType): CoreEecEventObject => ({
  events: {
    category: 'ecommerce',
    action: eventType,
    label: undefined,
  },
  event: eventType,
});

const getYMALTileType = (pageType: PageTypes = PageTypes.OTHER) => {
  const ymalTileTypes: Record<PageTypes, string> = {
    [PageTypes.CATEGORY]: YMAL_PLP,
    [PageTypes.SEARCH]: YMAL_SEARCH,
    [PageTypes.PRODUCT]: YMAL_PDP,
    [PageTypes.HOME]: YMAL_HP,
    [PageTypes.OTHER]: YMAL,
    [PageTypes.CAMPAIGN]: YMAL_CAMPAIGN,
    [PageTypes.LOOKBOOK]: YMAL_CAMPAIGN,
    [PageTypes.WISHLIST]: YMAL,
    [PageTypes.STORIES]: YMAL_STORIES,
    [PageTypes.PDV]: YMAL,
    [PageTypes.CUSTOMER_SERVICE]: YMAL,
    [PageTypes.CUSTOMER_SERVICE_RETURNS]: YMAL,
  };

  return ymalTileTypes[pageType] || YMAL;
};

export const getProductDataLayerEvent = (
  type: EventType,
  data: VendorEvent,
  isQuickView = false,
  quickViewProductTileType = ''
) => {
  let dataLayerEvent: VendorEvent | null = null;

  const getTileType = (sectionName: SectionNameType, pageType: PageTypes) => {
    const tileTypeMap: Record<SectionNameType, string> = {
      'you may also like': getYMALTileType(pageType),
      'recently viewed': RV,
      'complete the look': CTL,
      plp: PLP,
      srp: SRP,
      plp_RBP: PLP_RBP,
      wlp: WLP,
    };

    return tileTypeMap[sectionName] || CTL;
  };

  const getBaseProduct = (product: string): string => product?.split('-').slice(0, 3).join('-');

  switch (type) {
    case 'eecAddToCart': {
      const eventData = data as { product: Product; quantity: number };
      const dataLayerData = parseProduct(eventData.product);

      let productTileType = '';
      const materialNumber = getBaseProduct(
        eventData.product.code || eventData.product.baseProduct
      );

      if (storageAvailable('localStorage')) {
        productTileType =
          localStorage.getItem(`${PRODUCT_TILE_TYPE}-${materialNumber.toUpperCase()}`) || '';
      }

      dataLayerEvent = {
        ecommerce: {
          add: {
            products: [
              {
                ...dataLayerData,
                quantity: eventData.quantity,
                viewType: data.viewType || 'general view',
              },
            ],
          },
          currencyCode: eventData.product.price?.currencyIso,
          productTileType,
        },
        ...makeEventObject(type),
      };

      break;
    }
    case 'eecRemoveFromCart': {
      const eventData = data as CartEntryFieldsFragment;
      const cartProduct = eventData.product as Product;

      if (cartProduct) {
        const dataLayerData = parseProduct(cartProduct);

        dataLayerEvent = {
          ecommerce: {
            remove: {
              products: [
                {
                  ...dataLayerData,
                  quantity: eventData?.quantity,
                  removalType: 'user',
                },
              ],
            },
            currencyCode: cartProduct.price?.currencyIso,
          },
          ...makeEventObject(type),
        };
      }

      break;
    }
    case 'eecProductDetailView': {
      const eventData = data as Product;
      const dataLayerData = parseProduct(eventData);

      let productTileType = quickViewProductTileType;
      const materialNumber = eventData.code || eventData.baseProduct;

      if (!isQuickView && storageAvailable('localStorage')) {
        productTileType =
          localStorage.getItem(`${PRODUCT_TILE_TYPE}-${materialNumber.toUpperCase()}`) || '';
      }

      dataLayerEvent = {
        ecommerce: {
          currencyCode: eventData.price?.currencyIso,
          productTileType,
          detail: {
            products: [
              {
                ...dataLayerData,
                mainColorHex: eventData.mainColor?.facetColorStyle,
                comingSoon: eventData.comingSoon,
                viewType: isQuickView ? 'quick view' : 'general view',
                hasReviews: eventData.isProductHasReview,
              },
            ],
          },
        },
        ...makeEventObject(type),
      };

      break;
    }
    case 'eecProductClick': {
      const eventData = data as {
        product: Product | AlgoliaProductHit;
        position: number;
        sectionName: SectionNameType;
        viewType: ProductClickViewType;
        currencyCode?: string;
      };
      const dataLayerData = parseProduct(eventData.product);
      const tileType = getTileType(eventData.sectionName, data?.pageType);
      const {
        baseProduct,
        isGiftCard,
        oneSize,
        sizeInformation,
        ean,
        mainColor,
        price,
        code,
        comingSoon,
      } = eventData.product as Product;
      const { 'baseProduct.code': baseProductCodeAlgolia, facetColorStyle } =
        eventData.product as AlgoliaProductHit;
      const baseProductCode = baseProduct || baseProductCodeAlgolia;

      const materialNumber = getBaseProduct(eventData.product.code || baseProductCode);

      if (storageAvailable('localStorage')) {
        const storageKey = `${PRODUCT_TILE_TYPE}-${materialNumber.toUpperCase()}`;
        const prioritizedTiles = ['you may also like', 'complete the look', 'plp_RBP'];
        const storageValue = localStorage.getItem(storageKey) || '';
        const canOverwrite =
          prioritizedTiles.includes(eventData.sectionName) &&
          !(storageValue.includes('recommendations') || storageValue.includes(PLP_RBP));

        if (!storageValue || canOverwrite) {
          localStorage.setItem(storageKey, tileType);
        }
      }

      const isOneSize = oneSize || isGiftCard;
      const eanValue = sizeInformation?.sizes?.at(0)?.ean || ean;
      const productId = !baseProductCode ? `${code}-PC` : code;
      const mainColorHex = mainColor?.facetColorStyle || facetColorStyle;

      dataLayerEvent = {
        ecommerce: {
          productTileType: tileType,
          currencyCode: price?.currencyIso || eventData?.currencyCode,
          click: {
            products: [
              {
                ...dataLayerData,
                comingSoon,
                mainColorHex,
                position: eventData.position + 1,
                viewType: eventData.viewType,
                ean: isOneSize ? eanValue : undefined,
                size: isOneSize ? 'PC' : undefined,
                ecomm_prodid: isOneSize ? productId : undefined,
              },
            ],
          },
        },
        ...makeEventObject(type),
      };

      break;
    }
    case 'eecImpressions': {
      const eventData = data as {
        eventProducts: EventProduct[];
        sectionName: SectionNameType;
        currency?: string | null;
      };
      const tileType = getTileType(eventData.sectionName, data?.pageType);

      dataLayerEvent = {
        ecommerce: {
          productTileType: tileType,
          currencyCode: eventData.currency,
          impressions: {
            products: eventData.eventProducts,
          },
        },
        ...makeEventObject(type),
      };

      break;
    }
    default:
      break;
  }

  return dataLayerEvent;
};

export const parseProductForImpressions = (
  product: Product | AlgoliaProductHit,
  position: number,
  fromProductList = false
) => {
  const dataLayerData = parseProduct(product, fromProductList);
  const mainColorHex =
    (product as Product).mainColor?.facetColorStyle ||
    (product as AlgoliaProductHit)?.facetColorStyle;

  return {
    ...dataLayerData,
    comingSoon: product.comingSoon,
    mainColorHex,
    position,
  };
};

export const parsePageInfoDataLayer = (
  locale: string,
  language: string,
  slice: string | null | undefined,
  defaultCurrency: string | null | undefined,
  countryEn: Country,
  pageType?: PageTypes
) => {
  const correlationId = getCookie('_rsu') as string | null | undefined;
  const languageFullEn = countryEn.supportedLanguages?.find(
    ln => ln?.isocode?.toLowerCase() === locale
  )?.name;
  const page: { [k: string]: boolean | string | number | undefined | null } = {
    slice,
    viewportWidth: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
    viewportHeight: Math.max(document.documentElement.clientHeight, window.innerHeight || 0),
    currencyCode: defaultCurrency,
    locale,
    country: countryEn?.name,
    language: languageFullEn,
    countryCode: countryEn.isocode,
    languageCode: language,
    pageType: pageType || PageTypes.OTHER,
    timestamp: Date.now() / 1e3,
    correlationId,
  };

  Object.keys(page).forEach(key => {
    if (page[key] === null || page[key] === undefined) {
      delete page[key];
    }
  });

  return page;
};

type parseUserInfoDataLayerProps = {
  commonData: UserContextType['commonData'];
};

export const parseUserInfoDataLayer = ({ commonData }: parseUserInfoDataLayerProps) => {
  try {
    const consentLevel = getCookie(COCO) as string | null | undefined;
    const user: { [k: string]: boolean | string | undefined | null } = {
      assistedSaleUserType: commonData?.assistedSaleUserType,
      assistedSaleUserID: commonData?.assistedSaleUserID,
      internal: commonData?.internal,
      loggedInStatus: commonData?.loggedInStatus,
      gender: commonData?.gender,
      customerType: commonData?.customerType,
      userID: commonData?.hashedUserId,
      accountType: commonData?.accountType?.toLowerCase(),
      ageGroup: commonData?.ageGroup,
      consentLevel: consentLevel || '00000',
      hashedIp: commonData?.hashedIp,
      thresholdCustomerType: commonData?.thresholdCustomerType,
    };

    Object.keys(user).forEach(key => {
      if (user[key] === null || user[key] === undefined) {
        delete user[key];
      }
    });

    return user;
  } catch (error) {
    return {};
    // TODO error handling
  }
};

export const getProductSize = (code: string): string => {
  const match = code.match(PRODUCT_CODE_REGEXP);

  return match && match[4] ? match[4].toUpperCase() : '';
};

export const parseCartUpdateDataLayer = (
  action: 'add' | 'remove',
  cartData: CartMutation,
  cartProduct?: Maybe<CartProduct>
) => {
  let modifiedProduct: Maybe<CartProduct> | undefined;
  let entriesProperties = {};
  let basicProperties = {};

  if (action === 'add' && cartData.cart && cartData.cartModifications) {
    modifiedProduct = cartData?.cartModifications[0]?.entry?.product;
  } else if (action === 'remove' && cartProduct) {
    modifiedProduct = cartProduct;
  }

  if (modifiedProduct) {
    const rsu = getCookie(RSU)?.toString() || '';
    const timestamp = Date.now() / 1e3;
    const event_uid = `web_cart_update_${rsu}_${timestamp}`;

    const {
      stockNotificationMessageEN,
      price,
      fromPrice,
      ean,
      fitEN,
      mainColor,
      materialGroup,
      baseProduct,
      code,
      styleFamilyEN,
      nameEN,
    } = modifiedProduct;

    const parentIDPattern = /^(\w+)-(\w+)/;
    const match = baseProduct?.match(parentIDPattern);
    const parent_id = match ? `${match[1]}-${match[2]}`.toLowerCase() : '';

    basicProperties = {
      action,
      availability_en: stockNotificationMessageEN,
      base_price_local_currency: price?.value,
      cart_update_type: 'user',
      cart_id: cartData.cart?.guid,
      domain: window.location.origin,
      ean,
      event_uid,
      fit_en: fitEN,
      from_price_local_currency: fromPrice?.value,
      local_currency: price?.currencyIso,
      main_color_en: mainColor?.descriptionEN || mainColor?.description,
      material_group: materialGroup,
      page_type: 'product',
      parent_id,
      product_id: baseProduct?.toLowerCase(),
      size: getProductSize(code),
      style_family_en: styleFamilyEN,
      timestamp,
      total_price_local_currency: cartData.cart?.totalPrice?.value,
      total_quantity: cartData.cart?.totalItems,
      variant_id: code,
      variant_name: nameEN,
    };
  }

  if (cartData.cart?.entries) {
    type ParsedProducts = {
      [k: string]: number;
    };

    const parsedProducts = cartData.cart.entries.reduce((prev, current) => {
      const parsed = prev;

      if (current?.product?.baseProduct) {
        const productID = current?.product?.baseProduct.toLowerCase();

        if (prev[productID]) {
          parsed[productID] = prev[productID] + (current.quantity || 0);
        } else {
          parsed[productID] = current.quantity || 0;
        }
      }

      return parsed;
    }, {} as ParsedProducts);

    const variantProducts = cartData.cart.entries.map(entry => ({
      variant_id: entry?.product?.code.toLowerCase(),
      quantity: entry?.quantity,
    }));

    entriesProperties = {
      product_ids: Object.keys(parsedProducts),
      product_list: Object.entries(parsedProducts).map(([product_id, quantity]) => ({
        product_id,
        quantity,
      })),
      variant_ids: variantProducts.map(variantProduct => variantProduct.variant_id),
      variant_list: variantProducts,
    };
  }

  return {
    ...basicProperties,
    ...entriesProperties,
  };
};

const trackingOptions: {
  [key in SearchTrackingType]: {
    action: string;
    event: string;
  };
} = {
  click: { action: 'click', event: 'search-click' },
  enter: { action: 'enter', event: 'search-enter' },
  'page loaded': { action: 'page loaded', event: 'searchpageload' },
};

export const parseInternalSearchDataLayer = (
  option: SearchTrackingType,
  searchTerm: string,
  suggestion?: string
) => ({
  events: {
    category: 'search',
    action: trackingOptions[option].action,
    label: suggestion,
  },
  page: {
    searchTerm,
  },
  event: trackingOptions[option].event,
});

export const parseSortingOptionsDataLayer = (label: string) => ({
  events: {
    category: 'sorting options',
    action: 'click',
    label,
  },
  event: 'sortingoption-click',
});

type LocaleSelectorAction = 'open' | 'close' | 'submit';

export const parseLocaleSelectorDataLayer = (
  action: LocaleSelectorAction,
  redirectPath?: string
) => ({
  events: {
    category: 'select_locale',
    action,
  },
  event: 'select_locale',
  eventCallback: () => {
    if (redirectPath) {
      window.location.href = redirectPath;
    }
  },
});
